import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, Minus, Plus, ArrowRight } from 'lucide-react';
import Header from './components/Header10';
import Footer from './components/Footer10';

const Cart10 = () => {
    const navigate = useNavigate();

    const [cartItems, setCartItems] = useState([
        {
            id: 1,
            name: 'Handwoven Cotton Saree',
            price: 4990,
            quantity: 1,
            size: 'M',
            image: 'https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=400',
        },
        {
            id: 2,
            name: 'Linen Kurta Set',
            price: 3490,
            quantity: 1,
            size: 'L',
            image: 'https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=400',
        },
    ]);

    const updateQuantity = (id, delta) => {
        setCartItems(prev =>
            prev.map(item =>
                item.id === id
                    ? { ...item, quantity: Math.max(1, item.quantity + delta) }
                    : item
            )
        );
    };

    const removeItem = (id) => {
        setCartItems(prev => prev.filter(item => item.id !== id));
    };

    const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

    return (
        <>
            <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&display=swap" rel="stylesheet" />

            <div style={{ fontFamily: "'Inter', sans-serif" }} className="min-h-screen bg-white">
                <Header cartCount={cartItems.length} />

                <main className="pt-16 md:pt-[72px]">
                    {/* Page Header */}
                    <div className="py-10 md:py-16 px-5 md:px-8 text-center" style={{ borderBottom: '1px solid #E7E5E4' }}>
                        <h1 className="text-2xl md:text-3xl font-light text-[#1C1917] tracking-tight">Your Cart</h1>
                        <p className="text-[#A8A29E] text-xs font-light mt-2">
                            {cartItems.length} {cartItems.length === 1 ? 'item' : 'items'}
                        </p>
                    </div>

                    <div className="max-w-[900px] mx-auto px-5 md:px-8 py-8 md:py-12">
                        {cartItems.length === 0 ? (
                            <div className="text-center py-20">
                                <p className="text-[#78716C] text-sm font-light mb-6">Your cart is empty</p>
                                <button
                                    onClick={() => navigate('/template10/shop')}
                                    className="inline-flex items-center gap-3 text-[#78716C] text-xs tracking-[2px] uppercase font-normal hover:text-[#1C1917] transition-colors duration-300 bg-transparent border-none cursor-pointer"
                                >
                                    Continue Shopping <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                                </button>
                            </div>
                        ) : (
                            <>
                                {/* Cart Items */}
                                <div className="space-y-0">
                                    {cartItems.map((item) => (
                                        <div
                                            key={item.id}
                                            className="flex gap-4 md:gap-6 py-6"
                                            style={{ borderBottom: '1px solid #E7E5E4' }}
                                        >
                                            {/* Image */}
                                            <div className="w-20 md:w-28 aspect-[3/4] overflow-hidden bg-[#FAFAF9] flex-shrink-0">
                                                <img
                                                    src={item.image}
                                                    alt={item.name}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>

                                            {/* Details */}
                                            <div className="flex-1 flex flex-col justify-between">
                                                <div>
                                                    <h3 className="text-[#1C1917] text-sm font-light mb-1">{item.name}</h3>
                                                    <p className="text-[#A8A29E] text-xs font-light">Size: {item.size}</p>
                                                </div>

                                                <div className="flex items-center justify-between mt-4">
                                                    {/* Quantity */}
                                                    <div className="flex items-center gap-3">
                                                        <button
                                                            onClick={() => updateQuantity(item.id, -1)}
                                                            className="w-7 h-7 flex items-center justify-center text-[#78716C] hover:text-[#1C1917] transition-colors bg-transparent cursor-pointer"
                                                            style={{ border: '1px solid #E7E5E4' }}
                                                        >
                                                            <Minus className="w-3 h-3" strokeWidth={1.5} />
                                                        </button>
                                                        <span className="text-[#1C1917] text-xs font-light w-6 text-center">{item.quantity}</span>
                                                        <button
                                                            onClick={() => updateQuantity(item.id, 1)}
                                                            className="w-7 h-7 flex items-center justify-center text-[#78716C] hover:text-[#1C1917] transition-colors bg-transparent cursor-pointer"
                                                            style={{ border: '1px solid #E7E5E4' }}
                                                        >
                                                            <Plus className="w-3 h-3" strokeWidth={1.5} />
                                                        </button>
                                                    </div>

                                                    {/* Price & Remove */}
                                                    <div className="flex items-center gap-4">
                                                        <span className="text-[#78716C] text-sm font-light">
                                                            ₹{(item.price * item.quantity).toLocaleString('en-IN')}
                                                        </span>
                                                        <button
                                                            onClick={() => removeItem(item.id)}
                                                            className="text-[#A8A29E] hover:text-[#1C1917] transition-colors bg-transparent border-none cursor-pointer p-0"
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Summary */}
                                <div className="mt-8 md:mt-12 md:max-w-sm md:ml-auto">
                                    <div className="flex justify-between items-center mb-3">
                                        <span className="text-[#78716C] text-sm font-light">Subtotal</span>
                                        <span className="text-[#1C1917] text-sm font-light">₹{subtotal.toLocaleString('en-IN')}</span>
                                    </div>
                                    <div className="flex justify-between items-center mb-6">
                                        <span className="text-[#A8A29E] text-xs font-light">Shipping calculated at checkout</span>
                                    </div>

                                    <div className="w-full h-px bg-[#E7E5E4] mb-6" />

                                    <div className="flex justify-between items-center mb-8">
                                        <span className="text-[#1C1917] text-sm font-normal">Total</span>
                                        <span className="text-[#1C1917] text-lg font-light">₹{subtotal.toLocaleString('en-IN')}</span>
                                    </div>

                                    <button
                                        onClick={() => navigate('/template10/checkout')}
                                        className="w-full py-3.5 bg-[#78716C] text-white text-xs tracking-[2px] uppercase font-normal hover:bg-[#57534E] transition-colors duration-300 cursor-pointer border-none mb-3"
                                    >
                                        Proceed to Checkout
                                    </button>

                                    <button
                                        onClick={() => navigate('/template10/shop')}
                                        className="w-full py-3 text-[#78716C] text-xs tracking-[1px] uppercase font-light hover:text-[#1C1917] transition-colors duration-300 bg-transparent border-none cursor-pointer"
                                    >
                                        Continue Shopping
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </main>

                <Footer />
            </div>
        </>
    );
};

export default Cart10;
